import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  UnorderedListOutlined,
  DollarCircleOutlined,
  TeamOutlined,
  LogoutOutlined,
  DownOutlined,
  CreditCardOutlined,
  KeyOutlined,
  CalendarOutlined,
  CarryOutOutlined,
  ScheduleOutlined,
  BarsOutlined,
  SnippetsOutlined,
  DollarOutlined,
  FileDoneOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
  FileOutlined,
  FileAddOutlined,
  LockOutlined,
  HomeOutlined,
  InboxOutlined,
  AppstoreAddOutlined,
  ProductOutlined,
  AppstoreOutlined,
  WhatsAppOutlined,
  ApiOutlined,
  FundOutlined,
  BarChartOutlined,
  DashboardOutlined,
  AlertOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import { LiaUserNurseSolid, LiaBuildingSolid } from 'react-icons/lia';
import { FaBuilding, FaTooth } from 'react-icons/fa';
import { RiHealthBookLine } from 'react-icons/ri';
import { Layout, Menu, Button, Dropdown, Space, MenuProps, Avatar } from 'antd';
import { useAuth } from '../../hooks/auth';
import {
  ContainerHeaderTitle,
  ContainerLogo,
  ContainerSider,
  HeaderEyebrow,
  Logo,
  SidebarFooter,
  SidebarUserButton,
  SidebarUserCard,
  SidebarUserMeta,
  TitleHeader,
} from './styles';
import logo from '../../assets/logo.png';
import ResetPassword from '../ResetPassword';
import { Screens } from '../../shared/enums/screens.enum';
import { useConfiguration } from '../../hooks/configuration';
import { hasAllowedModule } from '../../shared/utils/modules';
import LanguageSwitcher from '../LanguageSwitcher';
import { useI18n } from '../../shared/i18n';

const { Header, Content, Footer } = Layout;

interface ISideNavProps {
  children: React.ReactNode;
}

type SideNavItem = NonNullable<MenuProps['items']>[number];

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase',
  opacity: 0.75,
  cursor: 'default',
};

const compactMenuItems = (
  items: Array<SideNavItem | null | false | undefined>,
): SideNavItem[] =>
  items.flatMap((item) => {
    if (!item) {
      return [];
    }

    if ('children' in item && Array.isArray(item.children)) {
      const children = compactMenuItems(
        item.children as Array<SideNavItem | null | false | undefined>,
      );

      if (!children.length) {
        return [];
      }

      return [{ ...item, children }];
    }

    return [item];
  });

export const SideNav: React.FC<ISideNavProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clinic, isSuperAdmin, signOut, screenFind } = useAuth();
  const { configuration } = useConfiguration();
  const { t } = useI18n();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [modalReset, setModalReset] = useState<boolean>(false);
  const [pageSelected, setPageSelected] = useState<string>('0');
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const brandName = clinic?.trim() ? clinic.replace(/[-_]+/g, ' ') : 'Sigeclin';
  const userEmail = user?.email ?? '';
  const userName = user?.name ?? '';
  const userDisplayName = userName.split(' ')[0] || t('common.account');

  const items: MenuProps['items'] = useMemo(
    () => [
      {
        label: t('sideNav.userMenu.account'),
        key: '1',
        icon: <UserOutlined style={{ fontSize: 20 }} />,
        onClick: () => navigate('/profile'),
      },
      {
        label: t('sideNav.userMenu.resetPassword'),
        key: '2',
        style: { width: '100%' },
        icon: <KeyOutlined style={{ fontSize: 20 }} />,
        onClick: () => setModalReset(true),
      },
      {
        label: t('sideNav.userMenu.signOut'),
        key: '3',
        icon: <LogoutOutlined style={{ fontSize: 20 }} />,
        onClick: () => signOut(),
      },
    ],
    [navigate, signOut, t],
  );

  const userMenuItems = items;

  const menuItems: MenuProps['items'] = useMemo(() => {
    const hasAccess = (...screens: string[]) => screenFind(screens);

    const isModuleEnabled = (moduleKey: string): boolean => {
      const modules = configuration?.allowed_modules;
      if (!modules || modules.length === 0) return true;
      return hasAllowedModule(modules, moduleKey);
    };

    const overviewItems = compactMenuItems([
      hasAccess(Screens.dashboardView)
        ? {
            key: '0',
            icon: <HomeOutlined />,
            label: t('sideNav.menu.home'),
            onClick: () => navigate('/dashboard'),
          }
        : null,
    ]);

    const careItems = compactMenuItems([
      isModuleEnabled('agenda') && {
        key: '1',
        icon: <ScheduleOutlined />,
        label: t('sideNav.menu.schedules'),
        children: [
          hasAccess(Screens.schedulesView)
            ? {
                key: '1.1',
                icon: <CarryOutOutlined />,
                label: t('sideNav.menu.schedule'),
                onClick: () => navigate('/schedules'),
              }
            : null,
          hasAccess(Screens.schedulesStatusView)
            ? {
                key: '1.2',
                icon: <UnorderedListOutlined />,
                label: t('sideNav.menu.scheduleStatus'),
                onClick: () => navigate('/schedules_status'),
              }
            : null,
          hasAccess(Screens.holidaysView)
            ? {
                key: '1.3',
                icon: <CalendarOutlined />,
                label: t('sideNav.menu.holidays'),
                onClick: () => navigate('/holidays'),
              }
            : null,
        ],
      },
      isModuleEnabled('clientes') && hasAccess(Screens.clientsView)
        ? {
            key: '2',
            icon: <TeamOutlined />,
            label: t('sideNav.menu.clients'),
            onClick: () => navigate('/clients'),
          }
        : null,
      isModuleEnabled('odontologico') &&
      (configuration?.dental || !configuration?.allowed_modules?.length) &&
      hasAccess(Screens.odontologyView)
        ? {
            key: '3',
            icon: <FaTooth />,
            label: t('sideNav.menu.odontology'),
            onClick: () => navigate('/dentals'),
          }
        : null,
      isModuleEnabled('tratamentos') && hasAccess(Screens.treatmentsView)
        ? {
            key: '4',
            icon: <FileDoneOutlined />,
            label: t('sideNav.menu.treatments'),
            onClick: () => navigate('/treatments'),
          }
        : null,
      isModuleEnabled('atestados_medicos') && {
        key: '5',
        icon: <FileTextOutlined />,
        label: t('sideNav.menu.healthCertificatesControl'),
        children: [
          hasAccess(Screens.modelsHealthCertificatesView)
            ? {
                key: '5.1',
                icon: <FileOutlined />,
                label: t('sideNav.menu.templates'),
                onClick: () => navigate('/models_health_certificates'),
              }
            : null,
          hasAccess(Screens.healthCertificatesView)
            ? {
                key: '5.2',
                icon: <FileAddOutlined />,
                label: t('sideNav.menu.healthCertificates'),
                onClick: () => navigate('/health_certificates'),
              }
            : null,
        ],
      },
      isModuleEnabled('documentos') && {
        key: '6',
        icon: <FileTextOutlined />,
        label: t('sideNav.menu.documentsControl'),
        children: [
          hasAccess(Screens.modelsDocumentsView)
            ? {
                key: '6.1',
                icon: <FileOutlined />,
                label: t('sideNav.menu.templates'),
                onClick: () => navigate('/models_documents'),
              }
            : null,
          hasAccess(Screens.documentsView)
            ? {
                key: '6.2',
                icon: <FileTextOutlined />,
                label: t('sideNav.menu.documents'),
                onClick: () => navigate('/documents'),
              }
            : null,
          hasAccess(Screens.modelsDocumentsView)
            ? {
                key: '6.3',
                icon: <FileOutlined />,
                label: t('sideNav.menu.prescriptionTemplates'),
                onClick: () => navigate('/models_prescriptions'),
              }
            : null,
          hasAccess(Screens.documentsView)
            ? {
                key: '6.4',
                icon: <FileTextOutlined />,
                label: t('sideNav.menu.prescriptions'),
                onClick: () => navigate('/prescriptions'),
              }
            : null,
        ],
      },
      isModuleEnabled('formularios') && {
        key: '7',
        icon: <RiHealthBookLine size={15} />,
        label: t('sideNav.menu.formsControl'),
        children: [
          hasAccess(Screens.modelsFormsView)
            ? {
                key: '7.1',
                icon: <FileOutlined />,
                label: t('sideNav.menu.templates'),
                onClick: () => navigate('/models_forms'),
              }
            : null,
          hasAccess(Screens.formsView)
            ? {
                key: '7.2',
                icon: <FileTextOutlined />,
                label: t('sideNav.menu.medicalRecords'),
                onClick: () => navigate('/forms'),
              }
            : null,
        ],
      },
      isModuleEnabled('procedimentos') && hasAccess(Screens.proceduresView)
        ? {
            key: '8',
            icon: <UnorderedListOutlined />,
            label: t('sideNav.menu.procedures'),
            onClick: () => navigate('/procedures'),
          }
        : null,
    ]);

    const financeItems = compactMenuItems([
      isModuleEnabled('financeiro') && {
        key: '9',
        icon: <DollarOutlined />,
        label: t('sideNav.menu.financial'),
        children: [
          hasAccess(Screens.financialsPlansView)
            ? {
                key: '9.1',
                icon: <UnorderedListOutlined />,
                label: t('sideNav.menu.financialPlan'),
                onClick: () => navigate('/financial_plans'),
              }
            : null,
          hasAccess(Screens.paymentsMethodsView)
            ? {
                key: '9.2',
                icon: <CreditCardOutlined />,
                label: t('sideNav.menu.paymentMethods'),
                onClick: () => navigate('/payments_methods'),
              }
            : null,
          hasAccess(Screens.financialsView)
            ? {
                key: '9.3',
                icon: <DollarCircleOutlined />,
                label: t('sideNav.menu.payableReceivable'),
                onClick: () => navigate('/financials'),
              }
            : null,
          hasAccess(Screens.cashRegisterView)
            ? {
                key: '9.4',
                icon: <FundOutlined />,
                label: t('sideNav.menu.cashRegister'),
                onClick: () => navigate('/financial/cash-register'),
              }
            : null,
        ],
      },
      isModuleEnabled('guias') &&
      (hasAccess(Screens.guidesStatusView) || hasAccess(Screens.guidesView)) && {
        key: '10',
        icon: <SnippetsOutlined />,
        label: t('sideNav.menu.guidesControl'),
        children: [
          hasAccess(Screens.guidesStatusView)
            ? {
                key: '10.1',
                icon: <UnorderedListOutlined />,
                label: t('sideNav.menu.guideStatus'),
                onClick: () => navigate('/guides_status'),
              }
            : null,
          hasAccess(Screens.guidesView)
            ? {
                key: '10.2',
                icon: <SnippetsOutlined />,
                label: t('sideNav.menu.guides'),
                onClick: () => navigate('/guides'),
              }
            : null,
        ],
      },
      isModuleEnabled('profissionais') &&
      (hasAccess(Screens.specialtiesView) ||
        hasAccess(Screens.terminologiesView) ||
        hasAccess(Screens.professionalsView)) && {
        key: '11',
        icon: <LiaUserNurseSolid />,
        label: t('sideNav.menu.professionalsControl'),
        children: [
          hasAccess(Screens.specialtiesView)
            ? {
                key: '11.1',
                icon: <SafetyCertificateOutlined />,
                label: t('sideNav.menu.specialties'),
                onClick: () => navigate('/specialties'),
              }
            : null,
          hasAccess(Screens.terminologiesView)
            ? {
                key: '11.2',
                icon: <BarsOutlined />,
                label: t('sideNav.menu.terminologies'),
                onClick: () => navigate('/terminologies'),
              }
            : null,
          hasAccess(Screens.professionalsView)
            ? {
                key: '11.3',
                icon: <LiaUserNurseSolid />,
                label: t('sideNav.menu.professionals'),
                onClick: () => navigate('/professionals'),
              }
            : null,
        ],
      },
      isModuleEnabled('convenios') && hasAccess(Screens.covenantsView)
        ? {
            key: '12',
            icon: <BarsOutlined />,
            label: t('sideNav.menu.covenants'),
            onClick: () => navigate('/covenants'),
          }
        : null,
    ]);

    const adminItems = compactMenuItems([
      isModuleEnabled('usuarios') && {
        key: '13',
        icon: <SafetyCertificateOutlined />,
        label: t('sideNav.menu.usersControl'),
        children: [
          isModuleEnabled('permissoes') && hasAccess(Screens.accessGroupsView)
            ? {
                key: '13.1',
                icon: <LockOutlined />,
                label: t('sideNav.menu.accessGroup'),
                onClick: () => navigate('/access_groups'),
              }
            : null,
          hasAccess(Screens.usersView)
            ? {
                key: '13.2',
                icon: <TeamOutlined />,
                label: t('sideNav.menu.users'),
                onClick: () => navigate('/users'),
              }
            : null,
        ],
      },
      isModuleEnabled('produtos') && {
        key: 'sales-control',
        icon: <DollarCircleOutlined />,
        label: t('sideNav.menu.salesControl'),
        children: [
          hasAccess(Screens.quickSaleView)
            ? {
                key: 'sales-control.1',
                icon: <DollarCircleOutlined />,
                label: t('sideNav.menu.productSales'),
                onClick: () => navigate('/sale'),
              }
            : null,
          hasAccess(Screens.salesView)
            ? {
                key: 'sales-control.2',
                icon: <SnippetsOutlined />,
                label: t('sideNav.menu.salesTracking'),
                onClick: () => navigate('/sales'),
              }
            : null,
        ],
      },
      isModuleEnabled('estoque') && {
        key: '14',
        icon: <InboxOutlined />,
        label: t('sideNav.menu.inventoryControl'),
        children: [
          hasAccess(Screens.productsCategoriesView)
            ? {
                key: '14.1',
                icon: <AppstoreAddOutlined />,
                label: t('sideNav.menu.productTypes'),
                onClick: () => navigate('/products_categories'),
              }
            : null,
          hasAccess(Screens.productsView)
            ? {
                key: '14.2',
                icon: <ProductOutlined />,
                label: t('sideNav.menu.registerProducts'),
                onClick: () => navigate('/products'),
              }
            : null,
          hasAccess(Screens.inventoriesView)
            ? {
                key: '14.3',
                icon: <AppstoreOutlined />,
                label: t('sideNav.menu.inventoryBalance'),
                onClick: () => navigate('/inventories'),
              }
            : null,
        ],
      },
      isModuleEnabled('whatsapp') && hasAccess(Screens.whatsAppView)
        ? {
            key: '15',
            icon: <WhatsAppOutlined />,
            label: t('sideNav.menu.whatsapp'),
            children: [
              {
                key: '15.1',
                icon: <ApiOutlined />,
                label: t('sideNav.menu.connections'),
                onClick: () => navigate('/connections'),
              },
              {
                key: '15.2',
                icon: <BarChartOutlined />,
                label: t('sideNav.menu.monitoring'),
                onClick: () => navigate('/whatsapp/monitoring'),
              },
            ],
          }
        : null,
      isModuleEnabled('crm') &&
      (hasAccess(Screens.crmCampaignsView) ||
        hasAccess(Screens.crmTemplatesView) ||
        hasAccess(Screens.crmMessagingView) ||
        hasAccess(Screens.crcDashboardView) ||
        hasAccess(Screens.crcContactsView) ||
        hasAccess(Screens.crcPendenciesView) ||
        hasAccess(Screens.crcReportsView))
        ? {
            key: '18',
            icon: <BarChartOutlined />,
            label: t('sideNav.menu.relationship'),
            children: [
              hasAccess(Screens.crmCampaignsView)
                ? {
                    key: '18.1',
                    icon: <AppstoreAddOutlined />,
                    label: t('sideNav.menu.crmCampaigns'),
                    onClick: () => navigate('/crm/campaigns'),
                  }
                : null,
              hasAccess(Screens.crmTemplatesView)
                ? {
                    key: '18.2',
                    icon: <SnippetsOutlined />,
                    label: t('sideNav.menu.crmTemplates'),
                    onClick: () => navigate('/crm/templates'),
                  }
                : null,
              hasAccess(Screens.crmMessagingView)
                ? {
                    key: '18.3',
                    icon: <InboxOutlined />,
                    label: t('sideNav.menu.crmMessaging'),
                    onClick: () => navigate('/crm/messaging'),
                  }
                : null,
              hasAccess(Screens.crcDashboardView)
                ? {
                    key: '18.4',
                    icon: <DashboardOutlined />,
                    label: t('sideNav.menu.crcDashboard'),
                    onClick: () => navigate('/crc/dashboard'),
                  }
                : null,
              hasAccess(Screens.crcContactsView)
                ? {
                    key: '18.5',
                    icon: <TeamOutlined />,
                    label: t('sideNav.menu.crcContacts'),
                    onClick: () => navigate('/crc/contacts'),
                  }
                : null,
              hasAccess(Screens.crcPendenciesView)
                ? {
                    key: '18.6',
                    icon: <AlertOutlined />,
                    label: t('sideNav.menu.crcPendencies'),
                    onClick: () => navigate('/crc/pendencies'),
                  }
                : null,
              hasAccess(Screens.crcReportsView)
                ? {
                    key: '18.7',
                    icon: <PieChartOutlined />,
                    label: t('sideNav.menu.crcReports'),
                    onClick: () => navigate('/crc/reports'),
                  }
                : null,
            ],
          }
        : null,
      isModuleEnabled('empresa') && hasAccess(Screens.companyView)
        ? {
            key: '16',
            icon: <LiaBuildingSolid />,
            label: t('sideNav.menu.companyData'),
            onClick: () => navigate('/company'),
          }
        : null,
      isSuperAdmin
        ? {
            key: '17',
            icon: <FaBuilding />,
            label: t('sideNav.menu.companies'),
            onClick: () => navigate('/companies'),
          }
        : null,
    ]);

    const sections: SideNavItem[] = [];

    const appendSection = (
      key: string,
      label: string,
      items: SideNavItem[],
      dividerKey?: string,
    ) => {
      if (!items.length) {
        return;
      }

      if (dividerKey && sections.length) {
        sections.push({ key: dividerKey, type: 'divider' });
      }

      sections.push({
        key,
        label,
        disabled: true,
        style: sectionLabelStyle,
      });
      sections.push(...items);
    };

    appendSection(
      'section-overview',
      t('sideNav.sections.overview'),
      overviewItems,
    );
    appendSection(
      'section-care',
      t('sideNav.sections.care'),
      careItems,
      'divider-1',
    );
    appendSection(
      'section-finance',
      t('sideNav.sections.finance'),
      financeItems,
      'divider-2',
    );
    appendSection(
      'section-admin',
      t('sideNav.sections.admin'),
      adminItems,
      'divider-3',
    );

    return sections;
  }, [
    configuration?.dental,
    configuration?.allowed_modules,
    isSuperAdmin,
    navigate,
    screenFind,
    t,
  ]);

  useEffect(() => {
    const routeByKey: Array<{ key: string; routes: string[] }> = [
      { key: '0', routes: ['/dashboard'] },
      { key: '1.1', routes: ['/schedules'] },
      { key: '1.2', routes: ['/schedules_status'] },
      { key: '1.3', routes: ['/holidays'] },
      { key: '2', routes: ['/clients'] },
      { key: '3', routes: ['/dentals'] },
      { key: '4', routes: ['/treatments'] },
      { key: '5.1', routes: ['/models_health_certificates'] },
      { key: '5.2', routes: ['/health_certificates'] },
      { key: '6.1', routes: ['/models_documents'] },
      { key: '6.2', routes: ['/documents'] },
      { key: '6.3', routes: ['/models_prescriptions'] },
      { key: '6.4', routes: ['/prescriptions'] },
      { key: '7.1', routes: ['/models_forms'] },
      { key: '7.2', routes: ['/forms'] },
      { key: '8', routes: ['/procedures'] },
      { key: '9.1', routes: ['/financial_plans'] },
      { key: '9.2', routes: ['/payments_methods'] },
      { key: '9.3', routes: ['/financials'] },
      { key: '9.4', routes: ['/financial/cash-register'] },
      { key: '10.1', routes: ['/guides_status'] },
      { key: '10.2', routes: ['/guides'] },
      { key: '11.1', routes: ['/specialties'] },
      { key: '11.2', routes: ['/terminologies'] },
      { key: '11.3', routes: ['/professionals'] },
      { key: '12', routes: ['/covenants'] },
      { key: '13.1', routes: ['/access_groups'] },
      { key: '13.2', routes: ['/users'] },
      { key: 'sales-control.1', routes: ['/sale'] },
      { key: 'sales-control.2', routes: ['/sales'] },
      { key: '14.1', routes: ['/products_categories'] },
      { key: '14.2', routes: ['/products'] },
      { key: '14.3', routes: ['/inventories'] },
      { key: '15.1', routes: ['/connections'] },
      { key: '15.2', routes: ['/whatsapp/monitoring'] },
      { key: '18.1', routes: ['/crm/campaigns'] },
      { key: '18.2', routes: ['/crm/templates'] },
      { key: '18.3', routes: ['/crm/messaging'] },
      { key: '18.4', routes: ['/crc/dashboard'] },
      { key: '18.5', routes: ['/crc/contacts'] },
      { key: '18.6', routes: ['/crc/pendencies'] },
      { key: '18.7', routes: ['/crc/reports'] },
      { key: '16', routes: ['/company'] },
      { key: '17', routes: ['/companies'] },
    ];

    const matched = routeByKey.find(({ routes }) =>
      routes.some(
        (route) =>
          location.pathname === route ||
          location.pathname.startsWith(`${route}/`),
      ),
    );

    const nextKey = matched?.key || '0';
    setPageSelected(nextKey);

    const parentKey = nextKey.includes('.') ? nextKey.split('.')[0] : nextKey;
    setOpenKeys(parentKey !== nextKey ? [parentKey] : []);
  }, [location.pathname]);

  useEffect(() => {
    if (collapsed) {
      setOpenKeys([]);
    }
  }, [collapsed]);

  useEffect(() => {
    const emitResize = () => window.dispatchEvent(new Event('resize'));

    emitResize();

    const frameId = window.requestAnimationFrame(emitResize);
    const timeoutId = window.setTimeout(emitResize, 220);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [collapsed]);

  return (
    <>
      {user ? (
        <Layout style={{ minHeight: '100vh' }}>
          <ContainerSider
            trigger={null}
            collapsible
            collapsed={collapsed}
            breakpoint="lg"
            width={265}
            className="hide-scrollbar"
            style={{
              overflow: 'hidden',
              height: '100vh',
              position: 'sticky',
              insetInlineStart: 0,
              top: 0,
              bottom: 0,
            }}
          >
            <ContainerLogo>
              <Logo src={logo} />
              {!collapsed && (
                <ContainerHeaderTitle>
                  <HeaderEyebrow>{t('sideNav.internalPanel')}</HeaderEyebrow>
                  <TitleHeader>{brandName}</TitleHeader>
                </ContainerHeaderTitle>
              )}
            </ContainerLogo>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[pageSelected]}
              openKeys={collapsed ? [] : openKeys}
              onOpenChange={(keys) => setOpenKeys(keys as string[])}
              inlineIndent={14}
              items={menuItems}
            />
            <SidebarFooter>
              <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
                <SidebarUserButton type="button">
                  <SidebarUserCard>
                    <Avatar
                      size={collapsed ? 42 : 44}
                      icon={<UserOutlined />}
                    />
                    {!collapsed && (
                      <>
                        <SidebarUserMeta>
                          <strong>
                            {userName?.split(' ')[0] || t('common.user')}
                          </strong>
                          <span>{clinic || userEmail}</span>
                        </SidebarUserMeta>
                        <DownOutlined
                          style={{ color: '#94a3b8', marginLeft: 'auto' }}
                        />
                      </>
                    )}
                  </SidebarUserCard>
                </SidebarUserButton>
              </Dropdown>
            </SidebarFooter>
          </ContainerSider>
          <Layout style={{ minWidth: 0, flex: 1 }}>
            <Header
              style={{
                display: 'flex',
                padding: '0 14px 0 0',
                background: 'rgba(255,255,255,0.96)',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '10px 10px 0',
                borderRadius: '18px',
                border: '1px solid rgba(24, 90, 66, 0.08)',
                boxShadow: '0 10px 24px rgba(18, 62, 51, 0.05)',
              }}
            >
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed((prev) => !prev)}
                style={{
                  fontSize: '16px',
                  width: 64,
                  height: 64,
                  color: '#1e4f3c',
                }}
              />
              <Space size={12}>
                <LanguageSwitcher />
                <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
                  <SidebarUserButton type="button">
                    <SidebarUserCard>
                      <Avatar
                        size={40}
                        icon={<UserOutlined />}
                      />
                      <SidebarUserMeta>
                        <strong>{userDisplayName}</strong>
                        <span>{clinic || userEmail}</span>
                      </SidebarUserMeta>
                    </SidebarUserCard>
                  </SidebarUserButton>
                </Dropdown>
              </Space>
            </Header>
            <Content
              style={{
                margin: '10px',
                padding: '10px',
                borderRadius: '18px',
              }}
            >
              {children}
            </Content>
            <Footer style={{ textAlign: 'center', padding: 0, fontSize: 12, opacity: 0.7 }}>
              <strong>{brandName}</strong> &copy; {new Date().getFullYear()} &middot; {t('common.signOut').includes('Sair') ? 'Todos os direitos reservados' : 'All rights reserved'}
            </Footer>
          </Layout>
        </Layout>
      ) : (
        <></>
      )}
      <ResetPassword open={modalReset} close={() => setModalReset(false)} />
    </>
  );
};

export default SideNav;
